import React from "react";
import { AlertCircle, AlertTriangle, ArrowRight, CheckCircle2, ChevronLeft } from "lucide-react";
import { Dropdown, type DropdownOption } from "../Dropdown";
import {
  Check,
  Field,
  FieldLabel,
  FieldSub,
  Footer,
  HeadRow,
  List,
  Required,
  Row,
  Sample,
  Status,
} from "./FieldMappingList.styles";
import type { FieldMappingListProps, FieldMappingOption, FieldMappingStatus } from "./FieldMappingList.types";

const STATUS_ICON: Record<FieldMappingStatus, React.ReactNode> = {
  ok: <CheckCircle2 aria-hidden="true" />,
  need: <AlertCircle aria-hidden="true" />,
  warn: <AlertTriangle aria-hidden="true" />,
  off: null,
};

/** Ungrouped options first, then each group in the order it first appears; a grouped option names its group as its description. */
const toDropdownOptions = (options: readonly FieldMappingOption[]): DropdownOption<string>[] => {
  const groups = Array.from(new Set(options.map((option) => option.group).filter((group): group is string => Boolean(group))));
  const item = (option: FieldMappingOption): DropdownOption<string> => ({
    value: option.value,
    label: option.label,
    description: option.group,
    disabled: option.disabled,
  });
  return [...options.filter((option) => !option.group).map(item), ...groups.flatMap((group) => options.filter((option) => option.group === group).map(item))];
};

/** Fields mapped to source columns, each with a select, its check and a sample value. */
export const FieldMappingList: React.FC<FieldMappingListProps> = ({ id, headers, rows, direction = "from", sampleFirst = false, headAppearance = "default", onChange, footer, className }) => (
  <List className={className}>
    <HeadRow aria-hidden="true" $sampleFirst={sampleFirst} $plain={headAppearance === "plain"}>
      <span>{headers.field}</span>
      {sampleFirst ? (
        <>
          <span>{headers.check}</span>
          <span>{headers.source}</span>
          <span />
        </>
      ) : (
        <>
          <span />
          <span>{headers.source}</span>
          <span>{headers.check}</span>
        </>
      )}
    </HeadRow>
    {rows.map((row) => (
      <Row key={row.id} data-status={row.status} $sampleFirst={sampleFirst}>
        <Field>
          <FieldLabel as="span" id={`${id}-${row.id}`}>
            {row.label}
            {row.required ? <Required aria-hidden="true">*</Required> : null}
          </FieldLabel>
          {row.sub ? <FieldSub>{row.sub}</FieldSub> : null}
        </Field>
        {sampleFirst ? (
          <Sample $plain title={row.sample}>
            {row.sample}
          </Sample>
        ) : direction === "from" ? (
          <ChevronLeft aria-hidden="true" />
        ) : (
          <ArrowRight aria-hidden="true" />
        )}
        <Dropdown
          variant="picker"
          size="sm"
          ariaLabel={row.required ? `${row.label} (required)` : row.label}
          ariaDescribedBy={`${id}-${row.id}-check`}
          value={row.value}
          invalid={row.status === "need"}
          options={toDropdownOptions(row.options)}
          onSelect={(next) => onChange(row.id, next)}
          showScrollbar
        />
        <Check id={`${id}-${row.id}-check`}>
          <Status $status={row.status}>
            {STATUS_ICON[row.status]}
            <span>{row.note}</span>
          </Status>
          {row.sample && !sampleFirst ? <Sample title={row.sample}>{row.sample}</Sample> : null}
        </Check>
      </Row>
    ))}
    {footer ? <Footer>{footer}</Footer> : null}
  </List>
);

export default FieldMappingList;
